import { useCallback, useEffect, useMemo, useState } from "react";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { getEmailOptions, saveEmailOptions } from "../api";
import { isSameSettings } from "../emailOptionsText";
import type { EmailDirection, EmailOptions, EmailOptionsSettings, EmailTaskRule } from "../types";

/**
 * Loads the email options and keeps an editable draft, so changes are only applied on save.
 */
export const useEmailOptions = () => {
  const [options, setOptions] = useState<EmailOptions | null>(null);
  const [draft, setDraft] = useState<EmailOptionsSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const loaded = await getEmailOptions();
        setOptions(loaded);
        setDraft(loaded.settings);
      } catch {
        setLoadError("We couldn't load the email options. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const isDirty = useMemo(
    () => !!options && !!draft && !isSameSettings(options.settings, draft),
    [options, draft],
  );

  useEffect(() => {
    if (!isDirty) return;

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const setAutoCreateTask = useCallback((autoCreateTask: boolean) => {
    setDraft(current => current && { ...current, autoCreateTask });
  }, []);

  const updateRule = useCallback((direction: EmailDirection, changes: Partial<EmailTaskRule>) => {
    setDraft(current => current && { ...current, [direction]: { ...current[direction], ...changes } });
  }, []);

  const discard = useCallback(() => {
    if (options) setDraft(options.settings);
  }, [options]);

  const save = useCallback(async () => {
    if (!draft) return;

    setIsSaving(true);
    try {
      await saveEmailOptions(draft);
      setOptions(current => current && { ...current, settings: draft });
      showSuccessToast("Email options saved.");
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "We couldn't save the email options.");
    } finally {
      setIsSaving(false);
    }
  }, [draft]);

  return { options, draft, isLoading, loadError, isDirty, isSaving, setAutoCreateTask, updateRule, discard, save };
};
